import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, today, formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { QuickRange } from "../components/QuickRange";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import {
  Wallet, Landmark, HandCoins, TrendingDown, ArrowRightLeft, AlertTriangle, PiggyBank,
  Users2, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";

function monthStart() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }

function Stat({ icon: Icon, label, value, tone = "default", sub }) {
  const tones = {
    default: "text-primary bg-primary/5 border-primary/10",
    accent: "text-accent bg-accent/5 border-accent/15",
    danger: "text-destructive bg-destructive/5 border-destructive/15",
    good: "text-[hsl(var(--positive))] bg-[hsl(var(--positive))]/10 border-[hsl(var(--positive))]/15",
  };
  return (
    <div className={`rounded-xl border p-5 ${tones[tone]}`}>
      <div className="flex items-center justify-between"><span className="text-sm font-medium text-muted-foreground">{label}</span><Icon size={18} /></div>
      <div className="num text-2xl font-bold mt-2 text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function TrendChart({ monthly }) {
  const max = Math.max(1, ...monthly.flatMap((m) => [m.cash_in, m.cash_out]));
  return (
    <div className="rounded-xl border border-border bg-card p-5 animate-fade-up">
      <div className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><BarChart3 size={16} className="text-primary" /> Cash In vs Cash Out — last 6 months</div>
      <div className="flex items-end justify-between gap-3 h-52">
        {monthly.map((m) => (
          <div key={m.month} data-testid={`trend-${m.month}`} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full flex items-end justify-center gap-1 h-40">
              <div className="w-1/2 rounded-t bg-[hsl(var(--positive))] transition-all" style={{ height: `${(m.cash_in / max) * 100}%` }} title={`In ${inr(m.cash_in)}`} />
              <div className="w-1/2 rounded-t bg-destructive/70 transition-all" style={{ height: `${(m.cash_out / max) * 100}%` }} title={`Out ${inr(m.cash_out)}`} />
            </div>
            <span className="text-xs text-muted-foreground">{m.label}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[hsl(var(--positive))]" /> Cash In</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-destructive/70" /> Cash Out</span>
      </div>
    </div>
  );
}

function DepositDialog({ cashAvailable, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", date: today(), note: "" });
  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    try {
      await api.post("/cash-deposit", { amount: amt, date: form.date, note: form.note });
      toast.success("Cash deposited to bank");
      setOpen(false); setForm({ amount: "", date: today(), note: "" }); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="analytics-deposit-btn" className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><ArrowRightLeft size={16} /> Deposit Cash to Bank</button>
      </DialogTrigger>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle className="font-display text-2xl">Cash Deposit to Bank</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-muted p-3 text-sm flex justify-between"><span className="text-muted-foreground">Cash available</span><span className="num font-bold text-accent">{inr(cashAvailable)}</span></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Amount (₹)</label><input data-testid="deposit-amount" type="number" className={field} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><label className={label}>Date</label><input data-testid="deposit-date" type="date" className={field + " num"} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          </div>
          <div><label className={label}>Note</label><input data-testid="deposit-note" className={field} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. HDFC a/c ••1234" /></div>
          <p className="text-xs text-muted-foreground">Reduces cash in hand and increases bank balance.</p>
        </div>
        <DialogFooter><button data-testid="deposit-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Deposit</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const isSA = user?.platform_role === "superadmin";
  const canWrite = hasWrite(user);
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("all");

  const load = async (s = start, e = end, cid = companyId) => {
    if (isSA) {
      const res = await api.get("/admin/analytics", { params: { start: s, end: e, company_id: cid } });
      setData(res.data);
      if (res.data.companies) setCompanies(res.data.companies);
    } else {
      setData((await api.get("/analytics", { params: { start: s, end: e } })).data);
    }
  };
  const pick = (s, e) => { setStart(s); setEnd(e); load(s, e); };
  const onCompany = (cid) => { setCompanyId(cid); load(start, end, cid); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const b = data?.balances;
  const maxExp = Math.max(1, ...(data?.expenses_by_category || []).map((x) => x.amount));

  return (
    <Layout>
      <div className="p-8 max-w-6xl mx-auto">
        <PageHeader title="Reports & Analytics" subtitle={isSA ? "Monitor every company — expenses, cash, deposits, salary & credit." : "Monitor credit flow, expenses, salary and cash position."}>
          {!isSA && canWrite && b && <DepositDialog cashAvailable={b.cash_balance} onDone={() => load()} />}
        </PageHeader>

        <div className="rounded-xl border border-border bg-card p-4 mb-6 flex flex-wrap items-end gap-3 animate-fade-up">
          {isSA && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Company</div>
              <select data-testid="an-company" className="h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" value={companyId} onChange={(e) => onCompany(e.target.value)}>
                <option value="all">All Companies (combined)</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="w-full"><QuickRange onPick={pick} active={[start, end]} /></div>
          <div><div className="text-xs text-muted-foreground mb-1">From</div><input data-testid="an-start" type="date" className="h-10 px-3 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><div className="text-xs text-muted-foreground mb-1">To</div><input data-testid="an-end" type="date" className="h-10 px-3 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <button data-testid="an-apply" onClick={() => load()} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">Apply</button>
          {isSA && data?.company_name && <span className="text-xs text-muted-foreground ml-auto">Showing: <span className="font-semibold text-foreground">{data.company_name}</span></span>}
        </div>

        {data && b && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6 animate-fade-up">
              <Stat icon={Wallet} label="Cash in Hand" value={inr(b.cash_balance)} tone="accent" />
              <Stat icon={Landmark} label="Bank Balance" value={inr(b.bank_balance)} />
              <Stat icon={PiggyBank} label="Bank Deposits (period)" value={inr((data.deposits || []).reduce((s, d) => s + d.amount, 0))} tone="good" sub={`all-time ${inr(data.deposited_total)}`} />
              <Stat icon={HandCoins} label="Credit Outstanding" value={inr(data.credit.total_outstanding)} tone={data.credit.over_limit_count ? "danger" : "default"} sub={`${data.credit.parties.length} parties · ${data.credit.over_limit_count} over limit`} />
              <Stat icon={TrendingDown} label="Expenses (period)" value={inr(data.expenses_total)} tone="danger" />
              <Stat icon={Users2} label="Salary Paid (period)" value={inr(data.salary_total)} tone="danger" />
            </div>

            {data.monthly?.length > 0 && <div className="mb-6"><TrendChart monthly={data.monthly} /></div>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
              <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
                <div className="px-5 py-3 bg-muted font-display text-lg font-semibold flex items-center gap-2"><HandCoins size={16} className="text-accent" /> Credit Parties</div>
                <div className="divide-y divide-border max-h-[420px] overflow-auto">
                  {data.credit.parties.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No outstanding credit.</div>}
                  {data.credit.parties.map((p, i) => (
                    <div key={`${p.name}-${i}`} data-testid={`an-party-${p.name}`} className="px-5 py-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium flex items-center gap-2">{p.name}
                          {isSA && p.company && <span className="text-[10px] text-muted-foreground">· {p.company}</span>}
                          {p.over_limit && <span className="flex items-center gap-1 text-[10px] bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded-full"><AlertTriangle size={10} /> OVER</span>}
                        </span>
                        <span className={`num text-sm font-bold ${p.over_limit ? "text-destructive" : ""}`}>{inr(p.outstanding)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground num mt-1 flex flex-wrap gap-x-3">
                        <span>limit {p.credit_limit > 0 ? inr(p.credit_limit) : "—"}</span>
                        <span>recd (period) {inr(p.received_in_period)}</span>
                        <span>last pay {p.last_payment || "—"}</span>
                        {p.aging_days != null && <span className={p.aging_days > 60 ? "text-destructive" : ""}>aging {p.aging_days}d</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
                <div className="px-5 py-3 bg-muted font-display text-lg font-semibold flex items-center gap-2"><TrendingDown size={16} className="text-destructive" /> Top Expenses (period)</div>
                <div className="p-5 space-y-3 max-h-[420px] overflow-auto">
                  {data.expenses_by_category.length === 0 && <div className="text-center text-sm text-muted-foreground">No expenses in this period.</div>}
                  {data.expenses_by_category.map((e) => (
                    <div key={e.name}>
                      <div className="flex justify-between text-sm mb-1"><span className="truncate">{e.name}</span><span className="num font-semibold">{inr(e.amount)}</span></div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-destructive/70 rounded-full" style={{ width: `${(e.amount / maxExp) * 100}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
              <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
                <div className="px-5 py-3 bg-muted font-display text-lg font-semibold flex items-center gap-2"><Users2 size={16} className="text-destructive" /> Salary Paid</div>
                <div className="divide-y divide-border max-h-[300px] overflow-auto">
                  {(data.salary_payments || []).length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No salary payments in this period.</div>}
                  {(data.salary_payments || []).map((t) => (
                    <div key={t.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                      <div className="min-w-0"><div className="truncate font-medium">{t.party_name || t.description}</div><div className="num text-xs text-muted-foreground">{t.date} · {t.pay_method || t.mode}</div></div>
                      <span className="num font-semibold text-destructive">{inr(t.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
                <div className="px-5 py-3 bg-muted font-display text-lg font-semibold flex items-center gap-2"><PiggyBank size={16} className="text-[hsl(var(--positive))]" /> Bank Deposits</div>
                <div className="divide-y divide-border max-h-[300px] overflow-auto">
                  {(data.deposits || []).length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No bank deposits in this period.</div>}
                  {(data.deposits || []).map((t) => (
                    <div key={t.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                      <div className="min-w-0"><div className="truncate font-medium">{t.description || "Cash deposit"}</div><div className="num text-xs text-muted-foreground">{t.date}</div></div>
                      <span className="num font-semibold text-[hsl(var(--positive))]">{inr(t.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-5 animate-fade-up">
              <div className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><ArrowRightLeft size={16} className="text-primary" /> Cash & Bank Flow ({data.period.start} → {data.period.end})</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div><div className="text-muted-foreground text-xs">Cash In</div><div className="num font-bold text-[hsl(var(--positive))]">{inr(data.flow.cash_in)}</div></div>
                <div><div className="text-muted-foreground text-xs">Cash Out</div><div className="num font-bold text-destructive">{inr(data.flow.cash_out)}</div></div>
                <div><div className="text-muted-foreground text-xs">Bank In</div><div className="num font-bold text-[hsl(var(--positive))]">{inr(data.flow.bank_in)}</div></div>
                <div><div className="text-muted-foreground text-xs">Bank Out</div><div className="num font-bold text-destructive">{inr(data.flow.bank_out)}</div></div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
